/* -----------------Globals--------------- */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/* -----------------UI--------------- */
import Box from "@memorang/ui/components/Box";
import { Message } from "@memorang/ui/components/ChatUI/Message";
import { RecommendedChips } from "@memorang/ui/components/ChatUI/RecommendedChips";
import { RecommendedChipsSkeleton } from "@memorang/ui/components/ChatUI/RecommendedChipsSkeleton";
import { CustomListSubheader } from "@memorang/ui/components/CustomListSubheader";
import { FadeView } from "@memorang/ui/components/FadeView";

/* -----------------Helpers & Hooks--------------- */
import { useSearchContext } from "./contexts/SearchContext";

/* -----------------Types--------------- */
import type { FeedbackParams as FeedbackParamsType } from "../feedback/types";
import type { HandleTracking } from "../types";
import type { EpubParams } from "./types";

/* -----------------Child components--------------- */
import References from "./References";

/* -----------------Constants--------------- */
import { useSearchView } from "./hooks/useSearchView";

type SearchViewProps = {
	feedbackDialogParams?: FeedbackParamsType;
	handleTracking: HandleTracking;
	handleShowConversationMode?: () => void;
	subheader?: string;
	summarySubheader?: string;
	loader?: React.ReactNode;
	handleNavigateToReference?: (url: string) => void;
	setConstructEpubParams?: React.Dispatch<
		React.SetStateAction<EpubParams | undefined>
	>;
	filterSourceIds?: number[];
	searchContext?: string;
	handleSelectSummarizeChip?: () => void;
	referenceThumbnails?: Record<string, string>;
};

export const SearchView = ({
	feedbackDialogParams,
	handleTracking,
	handleShowConversationMode,
	subheader = "Recommended Questions",
	summarySubheader,
	loader,
	handleNavigateToReference,
	setConstructEpubParams,
	filterSourceIds,
	searchContext,
	handleSelectSummarizeChip,
}: SearchViewProps) => {
	const { t } = useTranslation();
	const {
		showReference,
		loading,
		streaming,
		summaryResponse,
		references = [],
	} = useSearchContext();

	const {
		handleSelectQuery,
		localRecommendedQueries,
		messageActions,
		messageFeedbackComponents,
		showRecommendedChipsLoading,
	} = useSearchView({
		feedbackDialogParams,
		handleTracking,
		handleShowConversationMode,
		setConstructEpubParams,
		filterSourceIds,
		searchContext,
		handleSelectSummarizeChip,
	});

	const shouldShowEbookReferences = useMemo(
		() => showReference && references.length > 0 && !loading && !streaming,
		[showReference, references, loading, streaming],
	);

	const showSubheader = subheader.length > 0;

	return (
		<Box gap={24}>
			<Message
				loading={loading}
				content={summaryResponse}
				streaming={streaming}
				subheader={summarySubheader}
				loader={loader}
				messageActions={messageActions}
				profileImageUri={null}
				containerProps={{
					paddingHorizontal: 0,
				}}
			/>

			{shouldShowEbookReferences && (
				<References
					references={references}
					handleNavigateToReference={handleNavigateToReference}
				/>
			)}
			<Box>
				{showSubheader && (
					<CustomListSubheader style={{ paddingLeft: 0 }}>
						{subheader}
					</CustomListSubheader>
				)}
				{showRecommendedChipsLoading ? (
					<RecommendedChipsSkeleton numLines={4} />
				) : (
					<FadeView duration={500}>
						<Box>
							<RecommendedChips
								showCustomResponse={Boolean(
									summaryResponse && summaryResponse.length > 0,
								)}
								customResponseLabel={t("assistant.chips.askFollowUp")}
								queries={localRecommendedQueries}
								handleSelectQuery={handleSelectQuery}
								noFlatList
							/>
						</Box>
					</FadeView>
				)}
			</Box>
			{messageFeedbackComponents}
		</Box>
	);
};
